import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiErrorDetail, imgSrc } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import {
  Sparkles,
  Loader2,
  Copy,
  Save,
  CalendarClock,
  Heart,
  MessageCircle,
  Send,
  Bookmark,
  Check,
  ImageIcon,
  Wand2,
} from "lucide-react";

const TONES = [
  { id: "streetwear", label: "Streetwear" },
  { id: "premium", label: "Premium" },
  { id: "fun", label: "Fun" },
  { id: "minimal", label: "Minimal" },
];
const LENGTHS = [
  { id: "court", label: "Court" },
  { id: "moyen", label: "Moyen" },
  { id: "long", label: "Long" },
];

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

export default function Composer() {
  const location = useLocation();
  const { activeId, activeClient } = useClient();
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(location.state?.product || null);
  const [tone, setTone] = useState("streetwear");
  const [length, setLength] = useState("moyen");
  const [extra, setExtra] = useState("");
  const [caption, setCaption] = useState("");
  const [hashtags, setHashtags] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [copied, setCopied] = useState(false);
  // AI visual
  const [visualMode, setVisualMode] = useState(location.state?.aiImage ? "ai" : "product"); // product | ai
  const [aiMode, setAiMode] = useState("dress"); // dress | original
  const [visualPrompt, setVisualPrompt] = useState("");
  const [aiImage, setAiImage] = useState(location.state?.aiImage || null); // { image_url, data_url }
  const [genVisual, setGenVisual] = useState(false);

  useEffect(() => {
    if (!activeId) return;
    api.get("/products", { params: { client_id: activeId } }).then(({ data }) => {
      setProducts(data);
      setSelected((prev) => {
        if (prev && data.some((p) => p.id === prev.id)) return prev;
        if (prev && location.state?.product) return prev;
        return data[0] || null;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const handle = activeClient?.instagram_handle || "votre.marque";
  const fullText =
    caption + (hashtags.length ? "\n\n" + hashtags.map((h) => `#${h}`).join(" ") : "");

  const previewImg =
    visualMode === "ai" && aiImage ? aiImage.data_url : imgSrc(selected?.image_url);
  const postImageUrl =
    visualMode === "ai" && aiImage ? aiImage.image_url : selected?.image_url;

  const generateVisual = async () => {
    if (!activeId) return toast.error("Sélectionne une marque");
    if (aiMode === "dress" && !selected) return toast.error("Sélectionne un produit à habiller");
    setGenVisual(true);
    try {
      const { data } = await api.post("/generate-visual", {
        client_id: activeId,
        mode: aiMode,
        product_id: aiMode === "dress" ? selected?.id : null,
        prompt: visualPrompt || null,
      });
      setAiImage({ image_url: data.image_url, data_url: data.data_url });
      setVisualMode("ai");
      toast.success("Visuel généré");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setGenVisual(false);
  };

  const generate = async () => {
    if (!activeId) return toast.error("Sélectionne une marque");
    const productTitle = selected?.title || visualPrompt || activeClient?.name;
    if (!productTitle) return toast.error("Sélectionne un produit ou décris le visuel");
    setGenerating(true);
    try {
      const { data } = await api.post("/generate-caption", {
        client_id: activeId,
        product_title: productTitle,
        product_price: selected?.price || null,
        tone,
        length,
        extra: extra || null,
      });
      setCaption(data.caption);
      setHashtags(data.hashtags);
      toast.success("Légende générée");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setGenerating(false);
  };

  const copyAll = async () => {
    const ok = await copyText(fullText);
    if (ok) {
      setCopied(true);
      toast.success("Copié dans le presse-papier");
      setTimeout(() => setCopied(false), 1500);
    } else {
      toast.error("Copie impossible — sélectionne le texte manuellement");
    }
  };

  const savePost = async (status) => {
    if (!activeId) return toast.error("Sélectionne une marque");
    if (!postImageUrl) return toast.error("Ajoute un visuel (produit ou IA)");
    if (!caption.trim()) return toast.error("Génère ou écris une légende");
    if (status === "scheduled" && !scheduleDate)
      return toast.error("Choisis une date de planification");
    setSaving(true);
    try {
      await api.post("/posts", {
        client_id: activeId,
        image_url: postImageUrl,
        product_title: selected?.title || "Visuel IA",
        caption,
        hashtags,
        status,
        scheduled_for:
          status === "scheduled" && scheduleDate
            ? new Date(scheduleDate).toISOString()
            : null,
      });
      toast.success(status === "scheduled" ? "Post planifié" : "Brouillon enregistré");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <>
      <PageHeader overline={`Création · @${handle}`} title="Nouveau Post" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
        {/* LEFT */}
        <div className="lg:col-span-8 p-6 lg:p-8 lg:border-r border-white/10 space-y-8">
          <section>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-4">
              1 · Visuel produit
            </p>
            {products.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Aucun produit — importe la galerie de cette marque d'abord.
              </p>
            ) : (
              <div className="flex gap-3 overflow-x-auto pb-2" data-testid="product-picker">
                {products.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setSelected(p)}
                    data-testid={`pick-${p.id}`}
                    className={`shrink-0 w-24 h-24 border rounded-sm bg-[#0d0d0d] overflow-hidden transition-all duration-150 ${
                      selected?.id === p.id
                        ? "border-accent ring-1 ring-accent"
                        : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    <img src={p.image_url} alt={p.title} className="w-full h-full object-contain p-1.5" />
                  </button>
                ))}
              </div>
            )}
          </section>

          <section>
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">
                1b · Visuel du post
              </p>
              <div className="flex gap-1 border border-white/15 rounded-sm p-0.5">
                <button
                  data-testid="visual-mode-product"
                  onClick={() => setVisualMode("product")}
                  className={`px-3 py-1.5 rounded-sm text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors ${
                    visualMode === "product"
                      ? "bg-white/10 text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <ImageIcon className="h-3.5 w-3.5" /> Photo produit
                </button>
                <button
                  data-testid="visual-mode-ai"
                  onClick={() => setVisualMode("ai")}
                  className={`px-3 py-1.5 rounded-sm text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors ${
                    visualMode === "ai"
                      ? "bg-white/10 text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Wand2 className="h-3.5 w-3.5" /> Visuel IA
                </button>
              </div>
            </div>

            {visualMode === "ai" && (
              <div className="space-y-4 border border-white/10 rounded-sm p-4 bg-white/[0.02]">
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid="ai-mode-dress"
                    onClick={() => setAiMode("dress")}
                    className={`px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-wider border transition-all duration-150 ${
                      aiMode === "dress"
                        ? "bg-primary border-primary text-primary-foreground"
                        : "border-white/15 text-muted-foreground hover:text-foreground hover:border-white/30"
                    }`}
                  >
                    Habiller le produit
                  </button>
                  <button
                    data-testid="ai-mode-original"
                    onClick={() => setAiMode("original")}
                    className={`px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-wider border transition-all duration-150 ${
                      aiMode === "original"
                        ? "bg-primary border-primary text-primary-foreground"
                        : "border-white/15 text-muted-foreground hover:text-foreground hover:border-white/30"
                    }`}
                  >
                    Visuel original
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {aiMode === "dress"
                    ? "L'IA garde le produit sélectionné et crée un décor/ambiance à la DA de la marque."
                    : "L'IA crée un visuel 100% original à partir de la DA (sans photo produit)."}
                </p>
                <textarea
                  data-testid="visual-prompt-input"
                  value={visualPrompt}
                  onChange={(e) => setVisualPrompt(e.target.value)}
                  rows={2}
                  placeholder="Décris l'ambiance / la direction artistique du visuel (optionnel) — ex : fond béton urbain, lumière dorée, ambiance été."
                  className="w-full bg-transparent border border-white/15 rounded-sm px-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors resize-y"
                />
                <button
                  data-testid="generate-visual-button"
                  onClick={generateVisual}
                  disabled={genVisual}
                  className="w-full bg-accent text-accent-foreground font-bold uppercase tracking-wider text-sm py-3 rounded-sm flex items-center justify-center gap-2 hover:bg-accent/85 hover:-translate-y-[1px] transition-all duration-150 disabled:opacity-50"
                >
                  {genVisual ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  {genVisual ? "Génération du visuel…" : "Générer le visuel IA"}
                </button>
                {aiImage && (
                  <div className="border border-white/10 rounded-sm overflow-hidden">
                    <img src={aiImage.data_url} alt="visuel IA" className="w-full aspect-square object-cover" data-testid="ai-visual-preview" />
                  </div>
                )}
              </div>
            )}
          </section>

          <section>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-4">
              2 · Ton & longueur
            </p>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {TONES.map((t) => (
                  <button
                    key={t.id}
                    data-testid={`tone-${t.id}`}
                    onClick={() => setTone(t.id)}
                    className={`px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-wider border transition-all duration-150 ${
                      tone === t.id
                        ? "bg-primary border-primary text-primary-foreground"
                        : "border-white/15 text-muted-foreground hover:text-foreground hover:border-white/30"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {LENGTHS.map((l) => (
                  <button
                    key={l.id}
                    data-testid={`length-${l.id}`}
                    onClick={() => setLength(l.id)}
                    className={`px-4 py-2 rounded-sm text-xs font-bold uppercase tracking-wider border transition-all duration-150 ${
                      length === l.id
                        ? "bg-accent border-accent text-accent-foreground"
                        : "border-white/15 text-muted-foreground hover:text-foreground hover:border-white/30"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
              <input
                data-testid="extra-input"
                value={extra}
                onChange={(e) => setExtra(e.target.value)}
                placeholder="Consigne supplémentaire (optionnel) — ex : lancement collection, promo -10%…"
                className="w-full bg-transparent border border-white/15 rounded-sm px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors"
              />
              <button
                data-testid="generate-caption-button"
                onClick={generate}
                disabled={generating}
                className="w-full bg-primary text-primary-foreground font-bold uppercase tracking-wider text-sm py-3.5 rounded-sm flex items-center justify-center gap-2 hover:bg-primary/80 hover:-translate-y-[1px] transition-all duration-150 disabled:opacity-50"
              >
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Générer avec l'IA
              </button>
            </div>
          </section>

          <section>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-4">
              3 · Légende
            </p>
            <textarea
              data-testid="caption-textarea"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={6}
              placeholder="La légende générée apparaîtra ici — tu peux la modifier librement."
              className="w-full bg-transparent border border-white/15 rounded-sm px-4 py-3 text-sm leading-relaxed outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors resize-y"
            />
            {hashtags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid="hashtags-list">
                {hashtags.map((h, i) => (
                  <span key={i} className="font-mono text-xs text-accent border border-accent/30 rounded-sm px-2 py-1">
                    #{h}
                  </span>
                ))}
              </div>
            )}
          </section>

          <section className="flex flex-wrap gap-3 pt-2">
            <button
              data-testid="copy-button"
              onClick={copyAll}
              className="flex items-center gap-2 border border-white/15 rounded-sm px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-white/[0.05] transition-colors"
            >
              {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
              Copier
            </button>
            <button
              data-testid="save-draft-button"
              onClick={() => savePost("draft")}
              disabled={saving}
              className="flex items-center gap-2 border border-white/15 rounded-sm px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-white/[0.05] transition-colors disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              Brouillon
            </button>
            <div className="flex items-center gap-2">
              <input
                data-testid="schedule-date-input"
                type="datetime-local"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                className="bg-transparent border border-white/15 rounded-sm px-3 py-2 text-xs outline-none focus:ring-1 focus:ring-primary [color-scheme:dark]"
              />
              <button
                data-testid="schedule-button"
                onClick={() => savePost("scheduled")}
                disabled={saving}
                className="flex items-center gap-2 bg-accent text-accent-foreground rounded-sm px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-accent/85 hover:-translate-y-[1px] transition-all duration-150 disabled:opacity-50"
              >
                <CalendarClock className="h-4 w-4" />
                Planifier
              </button>
            </div>
          </section>
        </div>

        {/* RIGHT: IG preview */}
        <div className="lg:col-span-4 p-6 lg:p-8">
          <div className="lg:sticky lg:top-24">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-4">
              Aperçu Instagram
            </p>
            <div className="border border-white/12 rounded-sm bg-secondary max-w-sm" data-testid="ig-preview">
              <div className="flex items-center gap-3 p-3 border-b border-white/10">
                <div
                  className="h-8 w-8 rounded-full flex items-center justify-center font-heading font-black text-xs uppercase"
                  style={{ background: activeClient?.color || "#14342B" }}
                >
                  {(activeClient?.name || "?")[0]}
                </div>
                <div className="leading-tight">
                  <p className="text-xs font-bold">{handle}</p>
                  {activeClient?.city && (
                    <p className="text-[10px] text-muted-foreground">{activeClient.city}</p>
                  )}
                </div>
              </div>
              <div className="aspect-square bg-[#0d0d0d] flex items-center justify-center">
                {previewImg ? (
                  <img src={previewImg} alt="post" className={`w-full h-full ${visualMode === "ai" && aiImage ? "object-cover" : "object-contain p-6"}`} />
                ) : (
                  <span className="text-xs text-muted-foreground">Aucun visuel</span>
                )}
              </div>
              <div className="p-3">
                <div className="flex items-center gap-4 mb-2">
                  <Heart className="h-5 w-5" />
                  <MessageCircle className="h-5 w-5" />
                  <Send className="h-5 w-5" />
                  <Bookmark className="h-5 w-5 ml-auto" />
                </div>
                <p className="text-xs whitespace-pre-wrap leading-relaxed break-words">
                  {caption ? (
                    <>
                      <span className="font-bold">{handle}</span> {caption}
                    </>
                  ) : (
                    <span className="text-muted-foreground">La légende apparaîtra ici…</span>
                  )}
                </p>
                {hashtags.length > 0 && (
                  <p className="text-xs text-[#4a9dff] mt-1.5 break-words">
                    {hashtags.map((h) => `#${h}`).join(" ")}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
